import React, { useState } from 'react';
import { challengesAPI } from '../../utils/api';
import './FlagEditor.css';

const FlagEditor = ({ challenge, onUpdate, userRole }) => {
  const [editing, setEditing] = useState(false);
  const [flags, setFlags] = useState(challenge.flags || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Check if user has permission to edit flags
  const canEdit = ['admin', 'recruiter'].includes(userRole);

  if (!canEdit) {
    return null; // Don't show editor if no permission
  }

  const handleAddFlag = () => {
    setFlags([...flags, {
      value: '',
      points: 100,
      description: ''
    }]);
  };

  const handleRemoveFlag = (index) => {
    setFlags(flags.filter((_, i) => i !== index));
  };

  const handleFlagChange = (index, field, value) => {
    const updated = [...flags];
    updated[index] = {
      ...updated[index],
      [field]: field === 'points' ? parseInt(value) || 0 : value
    };
    setFlags(updated);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    setSaving(true);

    // Validate
    for (const flag of flags) {
      if (!flag.value || !flag.value.trim()) {
        setError('All flags must have a value');
        setSaving(false);
        return;
      }
      if (!flag.points || flag.points <= 0) {
        setError('All flags must have positive points');
        setSaving(false);
        return;
      }
    }

    try {
      await challengesAPI.updateFlags(challenge._id, flags);
      setSuccess(true);
      setEditing(false);
      if (onUpdate) {
        onUpdate(flags);
      }
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update flags');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFlags(challenge.flags || []);
    setEditing(false);
    setError(null);
  };

  return (
    <div className="flag-editor">
      <div className="flag-editor-header">
        <h3>Challenge Flags</h3>
        {!editing && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setEditing(true)}
          >
            Edit Flags
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          Flags updated successfully!
        </div>
      )}

      {editing ? (
        <div className="flag-editor-form">
          {flags.map((flag, index) => (
            <div key={index} className="flag-item-edit">
              <div className="flag-item-number">#{index + 1}</div>
              <div className="flag-item-fields">
                <div className="form-group">
                  <label>Flag Value *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="FLAG{example_flag}"
                    value={flag.value}
                    onChange={(e) => handleFlagChange(index, 'value', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Points *</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    value={flag.points}
                    onChange={(e) => handleFlagChange(index, 'points', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Description (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Found SQL injection vulnerability"
                    value={flag.description || ''}
                    onChange={(e) => handleFlagChange(index, 'description', e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveFlag(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="flag-editor-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddFlag}
            >
              + Add Flag
            </button>

            <div className="flag-editor-buttons">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || flags.length === 0}
              >
                {saving ? 'Saving...' : 'Save Flags'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flag-list-view">
          {flags.length === 0 ? (
            <p className="text-muted">No flags configured</p>
          ) : (
            flags.map((flag, index) => (
              <div key={index} className="flag-item-view">
                <div className="flag-value">
                  <strong>Flag #{index + 1}:</strong>
                  <code>{flag.value}</code>
                </div>
                <div className="flag-meta">
                  <span className="flag-points">{flag.points} points</span>
                  {flag.description && (
                    <span className="flag-description">{flag.description}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FlagEditor;
